import React, { useEffect, useMemo, useState } from 'react';

/**
 * Smart Template Engine — Preview System keystone (Phase 1A).
 *
 * Pixel-accurate WEB mirror of the Android `TemplateView.kt`. Given a resolved
 * `template` payload block (same shape as buildTemplateBlock / the RTDB block) and
 * a wall `grid`, it renders each screen exactly as the device draws it:
 *   - vertical (TOP_BOTTOM) theme gradient
 *   - zones as % of a virtual screen canvas (1920×1080 landscape / 1080×1920 portrait)
 *   - header: logo + bilingual section title (accent, bold, size = screenMin×0.055 [26..84])
 *   - body: equal-weight rows; per row photo (0.78×rowH cover) + AR-over-EN names + accent price
 *   - section rotation (single-screen, rotate mode), font_key → font, text_shadow
 *
 * Data-source-agnostic: the `template` comes from buildTemplatePreviewPayload
 * (editor) OR straight from RTDB (live). Reused across 1A/1B/2A/3A/4.
 *
 * Pure presentational + read-only — ZERO sync contact.
 */

const FONT_FAMILY = {
  tajawal: "'Tajawal', sans-serif",
  cairo: "'Cairo Play', sans-serif",
  ibmplex: "'IBM Plex Sans Arabic', sans-serif",
  kufi: "'Noto Kufi Arabic', sans-serif",
  naskh: "'Noto Naskh Arabic', serif",
};

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function hexToRgba(hex, alpha = 1) {
  let h = (hex || '#000000').replace('#', '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  if (Number.isNaN(n) || h.length !== 6) return `rgba(0,0,0,${alpha})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** "ar  •  en" when both present, else whichever — matches TemplateView.bilingual(). */
function bilingual(ar, en) {
  const a = (ar || '').trim();
  const e = (en || '').trim();
  if (a && e) return `${a}  •  ${e}`;
  return a || e;
}

function shadowCss(kind) {
  if (kind === 'dark') return `0 3px 8px ${hexToRgba('#000000', 0.78)}`;
  if (kind === 'light') return `0 3px 8px ${hexToRgba('#FFFFFF', 0.71)}`;
  return 'none';
}

/** One screen, drawn at virtual resolution then CSS-scaled to fit. */
function ScreenPreview({ template, gridPosition, vw, vh, scale }) {
  const theme = template.theme || {};
  const zones = template.zones || {};
  const rotation = template.rotation || { mode: 'none', interval_ms: 8000 };

  const sections = useMemo(() => {
    const ps = template.per_screen || {};
    return (ps[String(gridPosition)] || ps['1'] || {}).sections || [];
  }, [template, gridPosition]);

  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [sections.length]);
  useEffect(() => {
    if (rotation.mode !== 'rotate' || sections.length <= 1) return undefined;
    const interval = Math.max(2000, Number(rotation.interval_ms) || 8000);
    const t = setInterval(() => setIdx((i) => (i + 1) % sections.length), interval);
    return () => clearInterval(t);
  }, [rotation.mode, rotation.interval_ms, sections.length]);

  const screenMin = Math.min(vw, vh);
  const grad = (theme.gradient && theme.gradient.length ? theme.gradient : ['#1A1A1A', '#000000']);
  const fontFamily = FONT_FAMILY[theme.font_key] || FONT_FAMILY.tajawal;
  const textColor = theme.text_color || '#FFFFFF';
  const accent = theme.accent_color || '#D4AF37';
  const shadow = shadowCss(theme.text_shadow);

  const zonePx = (z, dflt) => {
    const zz = z || dflt;
    return {
      left: (zz.start_margin / 100) * vw,
      top: (zz.top_margin / 100) * vh,
      width: (zz.width / 100) * vw,
      height: (zz.height / 100) * vh,
    };
  };
  const hz = zonePx(zones.header, { start_margin: 6, top_margin: 4, width: 88, height: 14 });
  const bz = zonePx(zones.body, { start_margin: 6, top_margin: 20, width: 88, height: 70 });

  const section = sections[clamp(idx, 0, Math.max(0, sections.length - 1))] || null;
  const items = section?.items || [];

  const titleSize = clamp(screenMin * 0.055, 26, 84);
  const rowHeight = items.length ? bz.height / items.length : bz.height;
  const nameSize = clamp(rowHeight * 0.34, 20, 56);
  const subSize = clamp(nameSize * 0.62, 14, 36);
  const gap = nameSize * 0.4; // proportional stand-in for Android dp(14)/dp(16) margins

  return (
    <div style={{
      width: vw * scale, height: vh * scale, overflow: 'hidden',
      borderRadius: 6 * scale, position: 'relative', flex: 'none',
    }}>
      <div dir="ltr" style={{
        position: 'absolute', top: 0, left: 0, width: vw, height: vh,
        transform: `scale(${scale})`, transformOrigin: 'top left',
        background: `linear-gradient(to bottom, ${grad[0]}, ${grad[grad.length - 1]})`,
        fontFamily,
      }}>
        {section && (
          <>
            {/* Header zone */}
            <div style={{ position: 'absolute', left: hz.left, top: hz.top, width: hz.width, height: hz.height,
              display: 'flex', alignItems: 'center' }}>
              {theme.logo_url ? (
                <img src={theme.logo_url} alt="" style={{ height: hz.height * 0.9, width: hz.height * 0.9,
                  objectFit: 'contain', marginInlineEnd: gap }} />
              ) : null}
              <div style={{ flex: 1, textAlign: 'center', color: accent, fontWeight: 700,
                fontSize: titleSize, direction: 'rtl', textShadow: shadow, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {bilingual(section.name_ar, section.name_en)}
              </div>
            </div>

            {/* Body zone */}
            <div style={{ position: 'absolute', left: bz.left, top: bz.top, width: bz.width, height: bz.height,
              display: 'flex', flexDirection: 'column' }}>
              {items.map((it) => (
                <div key={it.id} style={{ height: rowHeight, display: 'flex', alignItems: 'center', flex: 'none' }}>
                  {it.photo_url ? (
                    <img src={it.photo_url} alt="" style={{ width: rowHeight * 0.78, height: rowHeight * 0.78,
                      objectFit: 'cover', marginInlineEnd: gap, flex: 'none' }} />
                  ) : null}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {it.name_ar ? (
                      <div style={{ color: textColor, fontWeight: 700, fontSize: nameSize, direction: 'rtl',
                        textShadow: shadow, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name_ar}</div>
                    ) : null}
                    {it.name_en ? (
                      <div style={{ color: hexToRgba(textColor, 0.78), fontSize: subSize, direction: 'ltr',
                        textShadow: shadow, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.name_en}</div>
                    ) : null}
                    {bilingual(it.description_ar, it.description_en) ? (
                      <div style={{ color: hexToRgba(textColor, 0.6), fontSize: subSize * 0.9, direction: 'rtl',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {bilingual(it.description_ar, it.description_en)}</div>
                    ) : null}
                  </div>
                  {[it.price, it.currency].filter((x) => x && String(x).trim()).length ? (
                    <div style={{ color: accent, fontWeight: 700, fontSize: nameSize, marginInlineStart: gap,
                      textShadow: shadow, whiteSpace: 'nowrap', flex: 'none' }}>
                      {[it.price, it.currency].filter((x) => x && String(x).trim()).join(' ')}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * @param {object} template  resolved template payload block
 * @param {object} grid      { rows, cols, orientation }
 * @param {number} maxWidth  target wall width in px (height derived)
 */
export default function WallPreviewRenderer({ template, grid, maxWidth = 520 }) {
  const rows = Math.max(1, grid?.rows || 1);
  const cols = Math.max(1, grid?.cols || 1);
  const portrait = grid?.orientation === 'portrait';
  const vw = portrait ? 1080 : 1920;
  const vh = portrait ? 1920 : 1080;

  const bezel = 4; // rendered px between screens
  const screenRenderW = (maxWidth - (cols - 1) * bezel) / cols;
  const scale = screenRenderW / vw;

  if (!template) return null;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, ${vw * scale}px)`,
      gridTemplateRows: `repeat(${rows}, ${vh * scale}px)`,
      gap: bezel,
      background: '#000',
      padding: bezel,
      borderRadius: 10,
      width: 'fit-content',
    }}>
      {Array.from({ length: rows * cols }).map((_, i) => (
        <ScreenPreview key={i} template={template} gridPosition={i + 1} vw={vw} vh={vh} scale={scale} />
      ))}
    </div>
  );
}
